import { useState, useEffect } from 'react';
import { X, Link2, Video, User, Bell, Clock, Loader } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

const SOURCE_DEVICE_TYPES = [
  { value: 'DOOR',      label: 'Door / Reader' },
  { value: 'CAMERA',    label: 'Camera' },
  { value: 'INTERCOM',  label: 'Intercom' },
  { value: 'SENSOR',    label: 'Sensor' },
  { value: 'FR_ENGINE', label: 'FR Engine' },
  { value: 'NVR',       label: 'NVR / Recorder' },
];

const PRIORITIES = [
  { value: 'PRIMARY',    label: 'Primary' },
  { value: 'SECONDARY',  label: 'Secondary' },
  { value: 'CONTEXTUAL', label: 'Contextual' },
];

const TARGET_TYPES = [
  { value: 'CAMERA',             label: 'Camera',       icon: Video },
  { value: 'USER',               label: 'User',         icon: User },
  { value: 'NOTIFICATION_GROUP', label: 'Notification', icon: Bell },
  { value: 'WEBHOOK',            label: 'Webhook',      icon: Link2 },
];

const DEFAULT_FORM = {
  sourceSystem: '', sourceDeviceType: 'DOOR', sourceDeviceId: '',
  targetType: 'CAMERA', targetId: '',
  priority: 'SECONDARY', preRollSec: 5, postRollSec: 15, enabled: true,
};

export default function TargetPickerModal({ isOpen, onClose, onSave, prefill = null }) {
  const [form, setForm]                         = useState({ ...DEFAULT_FORM });
  const [availableSystems, setAvailableSystems] = useState([]);
  const [availableIntercoms, setAvailableIntercoms] = useState([]);
  const [targets, setTargets]                   = useState({ cameras: [], users: [] });
  const [loadingSources, setLoadingSources]     = useState(false);
  const [loadingTargets, setLoadingTargets]     = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [errors, setErrors]                     = useState({});

  // Load source integrations when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm({ ...DEFAULT_FORM, ...(prefill || {}) });
    setLoadingSources(true);
    fetchWithTenant('/api/investigations/devices/sources')
      .then(r => r.json())
      .then(data => {
        const systems = [];
        const seen = new Set();
        for (const a of (data.acs || [])) {
          if (!seen.has(a.sourceSystem)) {
            systems.push({ value: a.sourceSystem, label: a.displayName, deviceType: 'DOOR' });
            seen.add(a.sourceSystem);
          }
        }
        for (const v of (data.vms || [])) {
          if (!seen.has(v.sourceSystem)) {
            systems.push({ value: v.sourceSystem, label: v.displayName, deviceType: 'CAMERA' });
            seen.add(v.sourceSystem);
          }
        }
        const intercoms = data.intercoms || [];
        setAvailableIntercoms(intercoms);
        if (intercoms.length > 0 && !seen.has('CCM_INTERNAL')) {
          systems.push({ value: 'CCM_INTERNAL', label: 'Intercoms (CCM)', deviceType: 'INTERCOM' });
        }
        setAvailableSystems(systems);
      })
      .catch(() => {})
      .finally(() => setLoadingSources(false));
  }, [isOpen]);

  // Load targets when targetType changes
  useEffect(() => {
    if (!isOpen || !form.targetType) return;
    setLoadingTargets(true);
    fetchWithTenant(`/api/investigations/devices/targets?type=${form.targetType}`)
      .then(r => r.json())
      .then(data => setTargets(prev => ({ ...prev, ...data })))
      .catch(() => {})
      .finally(() => setLoadingTargets(false));
  }, [isOpen, form.targetType]);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  };

  const handleSystemChange = (value) => {
    const sys = availableSystems.find(s => s.value === value);
    set('sourceSystem', value);
    if (sys) set('sourceDeviceType', sys.deviceType);
    set('sourceDeviceId', '');
  };

  const validate = () => {
    const e = {};
    if (!form.sourceSystem)          e.sourceSystem   = 'Select a system';
    if (!form.sourceDeviceId.trim()) e.sourceDeviceId = 'Required';
    if (!form.targetId)              e.targetId       = 'Select a target';
    if (form.preRollSec  < 0 || form.preRollSec  > 300) e.preRollSec  = '0–300s';
    if (form.postRollSec < 0 || form.postRollSec > 300) e.postRollSec = '0–300s';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({
        sourceSystem: form.sourceSystem, sourceDeviceType: form.sourceDeviceType,
        sourceDeviceId: form.sourceDeviceId.trim(), targetType: form.targetType,
        targetId: form.targetId, priority: form.priority,
        preRollSec: Number(form.preRollSec), postRollSec: Number(form.postRollSec),
        enabled: form.enabled,
      });
      onClose();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const renderDeviceIdField = () => {
    if (form.sourceSystem === 'CCM_INTERNAL' && availableIntercoms.length > 0) {
      return (
        <div className="tpm-field">
          <label className="tpm-label">Intercom <span className="tpm-req">*</span></label>
          <select className={`tpm-select ${errors.sourceDeviceId ? 'tpm-err-border' : ''}`}
            value={form.sourceDeviceId} onChange={e => set('sourceDeviceId', e.target.value)}>
            <option value="">Select intercom…</option>
            {availableIntercoms.map(i => (
              <option key={i.deviceId} value={i.deviceId}>{i.displayName}</option>
            ))}
          </select>
          {errors.sourceDeviceId && <span className="tpm-err-msg">{errors.sourceDeviceId}</span>}
        </div>
      );
    }
    return (
      <div className="tpm-field">
        <label className="tpm-label">Device ID <span className="tpm-req">*</span></label>
        <input className={`tpm-input ${errors.sourceDeviceId ? 'tpm-err-border' : ''}`}
          type="text" placeholder="e.g. door UUID from your ACS dashboard"
          value={form.sourceDeviceId} onChange={e => set('sourceDeviceId', e.target.value)} />
        <span className="tpm-hint">Find door/reader IDs in your ACS dashboard under reader settings.</span>
        {errors.sourceDeviceId && <span className="tpm-err-msg">{errors.sourceDeviceId}</span>}
      </div>
    );
  };

  const renderTargetPicker = () => {
    if (loadingTargets) return (
      <div className="tpm-loading"><Loader size={14} className="tpm-spin" /><span>Loading…</span></div>
    );
    if (form.targetType === 'CAMERA') {
      const cams = targets.cameras || [];
      const vmsList = targets.vmsSystems || [];

      // No cameras came back but VMS integrations exist → VMS is probably
      // offline. Don't dead-end; let the user type a camera ID manually.
      if (!cams.length) {
        if (vmsList.length > 0) {
          return (
            <div className="tpm-field">
              <p className="tpm-no-items">
                Camera list unavailable — VMS may be offline. You can still select by entering a camera ID manually.
              </p>
              <input className={`tpm-input ${errors.targetId ? 'tpm-err-border' : ''}`}
                type="text"
                placeholder="Camera ID (from your VMS dashboard)"
                value={form.targetId} onChange={e => set('targetId', e.target.value)} />
              {errors.targetId && <span className="tpm-err-msg">{errors.targetId}</span>}
            </div>
          );
        }
        return <p className="tpm-no-items">No cameras found. Ensure a VMS integration is configured.</p>;
      }

      return (
        <div className="tpm-picker-list">
          {cams.map(c => (
            <button key={c.id} type="button"
              className={`tpm-picker-item ${form.targetId === c.id ? 'tpm-picker-selected' : ''}`}
              onClick={() => set('targetId', c.id)}>
              <Video size={13} />
              <span>{c.displayName}</span>
              {c.integrationName && <span className="tpm-picker-sub">{c.integrationName}</span>}
            </button>
          ))}
        </div>
      );
    }
    if (form.targetType === 'USER') {
      const users = targets.users || [];
      if (!users.length) return <p className="tpm-no-items">No users found for this tenant.</p>;
      return (
        <div className="tpm-picker-list">
          {users.map(u => (
            <button key={u.id} type="button"
              className={`tpm-picker-item ${form.targetId === u.id ? 'tpm-picker-selected' : ''}`}
              onClick={() => set('targetId', u.id)}>
              <User size={13} /><span>{u.displayName}</span>
              {u.email && <span className="tpm-picker-sub">{u.email}</span>}
            </button>
          ))}
        </div>
      );
    }
    return (
      <div className="tpm-field">
        <input className={`tpm-input ${errors.targetId ? 'tpm-err-border' : ''}`}
          type="text"
          placeholder={form.targetType === 'WEBHOOK' ? 'https://your-webhook-url' : 'Group ID'}
          value={form.targetId} onChange={e => set('targetId', e.target.value)} />
        {errors.targetId && <span className="tpm-err-msg">{errors.targetId}</span>}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="tpm-modal">
        <div className="tpm-header">
          <div className="tpm-title"><Link2 size={16} /><span>New Association</span></div>
          <button className="tpm-close" onClick={onClose} disabled={saving}><X size={16} /></button>
        </div>

        <div className="tpm-body">
          {/* SOURCE */}
          <div className="tpm-section">
            <p className="tpm-section-heading">Source Device</p>
            <p className="tpm-section-desc">The device whose events trigger the correlation action.</p>

            <div className="tpm-field">
              <label className="tpm-label">System <span className="tpm-req">*</span></label>
              {loadingSources ? (
                <div className="tpm-loading"><Loader size={13} className="tpm-spin" /><span>Loading integrations…</span></div>
              ) : availableSystems.length === 0 ? (
                <p className="tpm-no-items">No integrations configured. Add an ACS or VMS integration in Settings first.</p>
              ) : (
                <select className={`tpm-select ${errors.sourceSystem ? 'tpm-err-border' : ''}`}
                  value={form.sourceSystem} onChange={e => handleSystemChange(e.target.value)}>
                  <option value="">Select system…</option>
                  {availableSystems.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              )}
              {errors.sourceSystem && <span className="tpm-err-msg">{errors.sourceSystem}</span>}
            </div>

            {form.sourceSystem && form.sourceSystem !== 'CCM_INTERNAL' && (
              <div className="tpm-field">
                <label className="tpm-label">Device Type</label>
                <select className="tpm-select" value={form.sourceDeviceType}
                  onChange={e => set('sourceDeviceType', e.target.value)}>
                  {SOURCE_DEVICE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            )}

            {form.sourceSystem && renderDeviceIdField()}
          </div>

          {/* TARGET */}
          <div className="tpm-section">
            <p className="tpm-section-heading">Target</p>
            <p className="tpm-section-desc">What happens when the source event fires.</p>
            <div className="tpm-type-tabs">
              {TARGET_TYPES.map(t => {
                const Icon = t.icon;
                return (
                  <button key={t.value} type="button"
                    className={`tpm-type-tab ${form.targetType === t.value ? 'tpm-tab-active' : ''}`}
                    onClick={() => { set('targetType', t.value); set('targetId', ''); }}>
                    <Icon size={13} /><span>{t.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="tpm-field" style={{ marginTop: '0.75rem' }}>
              {renderTargetPicker()}
              {errors.targetId && !['WEBHOOK','NOTIFICATION_GROUP'].includes(form.targetType) && (
                <span className="tpm-err-msg">{errors.targetId}</span>
              )}
            </div>
          </div>

          {/* SETTINGS */}
          <div className="tpm-section">
            <p className="tpm-section-heading">Recording Window</p>
            <p className="tpm-section-desc">Seconds of footage to capture around the event (for camera bookmarks).</p>
            <div className="tpm-row-3">
              <div className="tpm-field">
                <label className="tpm-label"><Clock size={11} /> Pre-roll (s)</label>
                <input className={`tpm-input ${errors.preRollSec ? 'tpm-err-border' : ''}`}
                  type="number" min={0} max={300} value={form.preRollSec}
                  onChange={e => set('preRollSec', e.target.value)} />
                {errors.preRollSec && <span className="tpm-err-msg">{errors.preRollSec}</span>}
              </div>
              <div className="tpm-field">
                <label className="tpm-label"><Clock size={11} /> Post-roll (s)</label>
                <input className={`tpm-input ${errors.postRollSec ? 'tpm-err-border' : ''}`}
                  type="number" min={0} max={300} value={form.postRollSec}
                  onChange={e => set('postRollSec', e.target.value)} />
                {errors.postRollSec && <span className="tpm-err-msg">{errors.postRollSec}</span>}
              </div>
              <div className="tpm-field">
                <label className="tpm-label">Priority</label>
                <select className="tpm-select" value={form.priority}
                  onChange={e => set('priority', e.target.value)}>
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div className="tpm-toggle-row">
              <span className="tpm-label">Active</span>
              <button type="button"
                className={`tpm-toggle ${form.enabled ? 'tpm-toggle-on' : 'tpm-toggle-off'}`}
                onClick={() => set('enabled', !form.enabled)}>
                <span className="tpm-toggle-knob" />
              </button>
              <span className="tpm-toggle-hint">{form.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>

        <div className="tpm-footer">
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving || loadingSources}>
            {saving ? <><Loader size={13} className="tpm-spin" />Saving…</> : 'Create Association'}
          </button>
        </div>
      </div>
    </div>
  );
}
